// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import Button, { ButtonPresentationProps } from "@conklin/MobiusOverrides/Button/Button";
import { sharedDateTimeOptions, XNGAGE_CUSTOM_WIDGET } from "@conklin/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import MyProjectsEditListForm from "@insite/content-library/Widgets/MyLists/MyListsEditListForm";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

enum fields {
    title = "title",
    buttonLink = "buttonLink",
    buttonTitle = "buttonTitle",
}

interface MyProjectsOverviewStyles {
    wrapper?: InjectableCss;
    heading?: InjectableCss;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    body?: InjectableCss;
    createListModal?: ModalPresentationProps;
    listImage?: LazyImageProps;
    listCard?: InjectableCss;
    infoContainer?: InjectableCss;
    listTitle?: LinkPresentationProps;
    listInfoText?: TypographyPresentationProps;
    buttonViewLink?: ButtonPresentationProps;
    centeredContent?: InjectableCss;
    noItemsMessage?: TypographyPresentationProps;
}

export const myProjectsOverviewStyles: MyProjectsOverviewStyles = {};

const styles = myProjectsOverviewStyles;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonTitle]: string;
    };
}

const OverViewParameters = {
    page: 1,
    pageSize: 3,
    sort: "ModifiedOn DESC",
    expand: ["top3products"],
    wishListLinesSort: "mostRecent",
} as GetWishListsApiParameter;

const mapStateToProps = (state: ApplicationState) => {
    return {
        isAuthenticated: xIsAuthenticated(state),
        myProjectsPage: getPageLinkByPageType(state, "MyListsPage")?.url,
        myProjectsDetailsPage: getPageLinkByPageType(state, "MyListsDetailsPage")?.url,
        wishListsDataView: getWishListsDataView(state, OverViewParameters),
        language: state.context.session.language,
    };
};

const mapDispatchToProps = {
    loadWishLists,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const MyProjectsOverview: FC<Props> = ({
    fields: { title, buttonLink, buttonTitle },
    wishListsDataView,
    myProjectsPage,
    loadWishLists,
    isAuthenticated,
    language,
    myProjectsDetailsPage,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const history = useHistory();
    const linkUrl = useGetLink(buttonLink).url;
    useEffect(() => {
        if (!wishListsDataView.isLoading && !wishListsDataView.value) {
            loadWishLists(OverViewParameters);
        }
    }, [wishListsDataView]);
    if (!isAuthenticated) {
        return null;
    }

    const openModal = () => {
        setIsModalOpen(true);
    };

    const handlLinkClick = () => {
        const url = linkUrl || myProjectsPage;
        if (url) {
            history.push(url);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.heading}>
                <Typography {...styles.title}>{title || translate("My Lists")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.body}>
                {wishListsDataView.isLoading && (
                    <StyledWrapper {...styles.centeredContent}>
                        <LoadingSpinner />
                    </StyledWrapper>
                )}
                {!wishListsDataView.isLoading && wishListsDataView.value?.length === 0 ? (
                    <>
                        <Typography {...styles.noItemsMessage}>
                            {siteMessage("Custom_Empty_MyRecent_Projects_Message")}
                        </Typography>
                        <Button
                            onClick={openModal}
                            aria-label="overViewCreateList"
                            data-test-selector="overview-create-list"
                            {...styles.buttonViewLink}
                        >
                            {translate("Create a Project")}
                        </Button>
                    </>
                ) : (
                    <>
                        {wishListsDataView.value?.map(list => {
                            const date = getLocalizedDateTime({
                                dateTime: new Date(list.updatedOn),
                                language,
                                options: {
                                    ...sharedDateTimeOptions,
                                },
                            });
                            return (
                                <StyledWrapper {...styles.listCard} key={list.id}>
                                    {list.wishListLineCollection?.[0]?.productUri ? (
                                        <Clickable
                                            href={list.wishListLineCollection?.[0]?.productUri}
                                            aria-label="recent project product image"
                                        >
                                            <LazyImage
                                                src={list.wishListLineCollection?.[0]?.smallImagePath}
                                                altText={list.description}
                                                {...styles.listImage}
                                            />
                                        </Clickable>
                                    ) : (
                                        <LazyImage
                                            src={list.wishListLineCollection?.[0]?.smallImagePath}
                                            altText={list.description}
                                            {...styles.listImage}
                                        />
                                    )}
                                    <StyledWrapper {...styles.infoContainer}>
                                        <Link
                                            href={`${myProjectsDetailsPage}?id=${list.id}`}
                                            {...styles.listTitle}
                                            data-test-selector="list-name-link"
                                        >
                                            {list.name}
                                        </Link>
                                        <Typography {...styles.listInfoText}>{`${translate(
                                            "Updated",
                                        )} ${date} ${translate("by")} ${list.updatedByDisplayName}`}</Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                        <Button onClick={handlLinkClick} {...styles.buttonViewLink}>
                            {buttonTitle || "View All"}
                        </Button>
                    </>
                )}
            </StyledWrapper>
            <Modal
                headline={translate("Create List")}
                {...styles.createListModal}
                isOpen={isModalOpen}
                handleClose={closeModal}
            >
                <MyProjectsEditListForm onCancel={closeModal} onSubmit={closeModal} />
            </Modal>
        </StyledWrapper>
    );
};
const MyProjectsOverviewWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyProjectsOverview),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                editorTemplate: "TextField",
                name: fields.title,
                fieldType: "General",
                defaultValue: "My Recent Projects",
            },
            {
                editorTemplate: "TextField",
                name: fields.buttonTitle,
                fieldType: "General",
                defaultValue: "View My Projects",
            },
            {
                editorTemplate: "LinkField",
                name: fields.buttonLink,
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};
export default MyProjectsOverviewWidget;
